import { mapApiFormatToHotelFilter } from '@web-platform/shared-banana-utils-frontend/src/helpers/hotelFiltersMapper';
import {
  QUERY_RESPONSE_RECEIVED,
  QUERY_REQUEST_SENT,
} from '@web-platform/shared-universal-constants/src/network';
import { MICHELIN_HOTEL_FILTER_CHANGED } from '@web-platform/shared-universal-redux/src/actions/michelin';
import { HOTELS_FILTERS, translate } from '@web-platform/shared-universal-translations';
import {
  MICHELIN_GET_HOTEL_CARHIRE_CREATE_SEARCH,
  MICHELIN_GET_HOTEL_CARHIRE_POLLING,
} from '@web-platform/shared-universal-types/src/network/requestTypes';

import { USER_REQUESTED_SEARCH } from '../../../../actions/searchControls';
import { getHotelReviewRatingTranslation } from '../utils';

import type { SkyscannerMichelinSearchV1Search } from '@skyscanner-internal/michelin-web-http-client';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { Hotel } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/hotelResults';
import type {
  HotelBucket,
  MichelinHotelsState,
} from '@web-platform/shared-universal-types/src/redux/banana/state/shared/michelin/hotels';

type SearchResponse = SkyscannerMichelinSearchV1Search.SearchResponse;

export const INITIAL_STATE: MichelinHotelsState = {
  buckets: Object.values(HOTELS_FILTERS).reduce<Record<string, HotelBucket>>((acc, filter) => {
    acc[filter.bucket] = {
      id: filter.bucket,
      name: filter.label,
      items: [],
      // Set isLoading to true for BEST bucket (first bucket to be loaded) only to avoid fallback loading
      isLoading: filter.bucket === HOTELS_FILTERS.BEST.bucket,
      isFinished: false,
    };
    return acc;
  }, {}),
  selectedFilter: HOTELS_FILTERS.BEST.bucket,
};

const transformHotelData = (
  response: SearchResponse,
  state: MichelinHotelsState,
): MichelinHotelsState => {
  if (!response || !response.sections) return state;

  const accommodationsSection = response.sections.find(
    (section) => section && section.accommodations,
  );

  if (
    !accommodationsSection ||
    !accommodationsSection.accommodations ||
    !accommodationsSection.context
  ) {
    return state;
  }

  const { accommodations, context } = accommodationsSection;
  const { polling } = context;
  const bucketId = accommodations.bucket?.id || 'common_best_v1';

  const newBuckets = { ...state.buckets };

  if (polling) {
    newBuckets[bucketId] = {
      ...newBuckets[bucketId],
      isLoading: !polling.complete,
      isFinished: !!polling.complete,
    };
  }

  if (!polling?.complete || !polling?.modified) {
    return {
      ...state,
      buckets: newBuckets,
    };
  }

  const hotelItems: Hotel[] = [];

  if (accommodations.results) {
    // Use resultIds array to maintain order if available, otherwise fall back to Object.keys
    const hotelIds = accommodations.bucket?.resultIds || Object.keys(accommodations.results);

    hotelIds.forEach((hotelId: string) => {
      const hotel = accommodations.results?.[hotelId];
      if (!hotel) return;

      let rating = null;

      if (hotel.aggregateRating) {
        rating = {
          description: hotel.aggregateRating.fivePointsValue
            ? getHotelReviewRatingTranslation(hotel.aggregateRating.fivePointsValue)
            : '',
          count: hotel.aggregateRating.count || 0,
          value: hotel.aggregateRating.value || 0,
          taScore: hotel.aggregateRating.fivePointsValue || 0,
          maxScore: 5,
          taImage: hotel.aggregateRating.imageUri || '',
        };
      }

      let price = 0;
      if (hotel.price?.price?.amount) {
        price = parseInt(hotel.price.price.amount, 10);
      }

      const hotelEntity: Hotel = {
        hotelId: hotel.id || '',
        name: hotel.name || '',
        heroImage: hotel.imageUri || '',
        price,
        priceFrom: null,
        stars: hotel.stars || 0,
        nightsForPrice: translate('ktxtNightsPerNight'),
        deeplink: hotel.deeplink || '',
        rating,
      };

      hotelItems.push(hotelEntity);
    });
  }

  newBuckets[bucketId] = {
    ...newBuckets[bucketId],
    items: hotelItems,
  };

  return {
    ...state,
    buckets: newBuckets,
  };
};

export default (
  state: MichelinHotelsState = INITIAL_STATE,
  action: Action,
): MichelinHotelsState => {
  switch (action.type) {
    case QUERY_RESPONSE_RECEIVED: {
      const { payload } = action;

      if (
        (payload.type === MICHELIN_GET_HOTEL_CARHIRE_CREATE_SEARCH ||
          payload.type === MICHELIN_GET_HOTEL_CARHIRE_POLLING) &&
        payload.response?.body &&
        typeof payload.response.body === 'object' &&
        'sections' in payload.response.body
      ) {
        return transformHotelData(payload.response.body, state);
      }

      return state;
    }

    case QUERY_REQUEST_SENT: {
      const { payload } = action;
      if (payload.type === MICHELIN_GET_HOTEL_CARHIRE_CREATE_SEARCH) {
        const bucketId = mapApiFormatToHotelFilter(
          payload.variables?.hotelFilter?.toString() || '',
        );
        return {
          ...state,
          buckets: {
            ...state.buckets,
            [bucketId]: {
              ...state.buckets[bucketId],
              isLoading: true,
            },
          },
        };
      }
      return state;
    }

    case MICHELIN_HOTEL_FILTER_CHANGED: {
      const { payload } = action;
      const bucketId = payload.filterId;

      if (state.buckets[bucketId] && state.buckets[bucketId].isFinished === false) {
        return {
          ...state,
          buckets: {
            ...state.buckets,
            [payload.filterId]: {
              ...state.buckets[bucketId],
              isLoading: true,
            },
          },
          selectedFilter: bucketId,
        };
      }

      return state;
    }

    case USER_REQUESTED_SEARCH: {
      const { payload } = action;
      if (payload.searched) {
        return INITIAL_STATE;
      }
      return state;
    }

    default:
      return state;
  }
};

export const get = (state: MichelinHotelsState): MichelinHotelsState => state;
export const getSelectedFilter = (state: MichelinHotelsState): string => state.selectedFilter;
export const getBuckets = (state: MichelinHotelsState) => state.buckets;
